<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import ConversationAPI from 'dashboard/api/conversations';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { relativeActivityLabel } from './messagePreview';

const props = defineProps({
  displayId: {
    type: String,
    required: true,
  },
  accountId: {
    type: [Number, String],
    required: true,
  },
  isOwnMessage: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const router = useRouter();
const allLabels = useMapGetter('labels/getLabels');
const allInboxes = useMapGetter('inboxes/getInboxes');
const getConversationById = useMapGetter('getConversationById');
const getSelectedChat = useMapGetter('getSelectedChat');

// Module-level cache and in-flight map shared across all instances
const conversationCache = new Map();
const conversationRequests = new Map();

function conversationFromStore(displayId, selectedChat, findById) {
  if (
    selectedChat?.id != null &&
    String(selectedChat.id) === String(displayId)
  ) {
    return selectedChat;
  }
  if (typeof findById === 'function') {
    const found = findById(displayId);
    if (found?.id != null) return found;
  }
  return null;
}

async function loadConversation(displayId) {
  const cached = conversationCache.get(displayId);
  if (cached) return cached;
  if (conversationRequests.has(displayId)) {
    return conversationRequests.get(displayId);
  }

  const request = ConversationAPI.show(displayId)
    .then(({ data }) => {
      if (data) conversationCache.set(displayId, data);
      return data || null;
    })
    .finally(() => {
      conversationRequests.delete(displayId);
    });

  conversationRequests.set(displayId, request);
  return request;
}

const conversation = ref(
  conversationCache.get(props.displayId) ||
    conversationFromStore(
      props.displayId,
      getSelectedChat.value,
      getConversationById.value
    )
);
const fetchFailed = ref(false);
const isLoading = computed(() => !conversation.value && !fetchFailed.value);

const contactName = computed(
  () => conversation.value?.meta?.sender?.name || ''
);
const contactThumbnail = computed(
  () => conversation.value?.meta?.sender?.thumbnail || ''
);
const inboxName = computed(() => {
  const inboxId = conversation.value?.inbox_id;
  if (!inboxId) return '';
  return allInboxes.value?.find(inbox => inbox.id === inboxId)?.name || '';
});
const assigneeName = computed(
  () => conversation.value?.meta?.assignee?.name || ''
);
const status = computed(() => conversation.value?.status || 'open');
const lastActivityAt = computed(() =>
  relativeActivityLabel(conversation.value?.last_activity_at)
);
const lastMessageObj = computed(() => {
  return (
    conversation.value?.last_non_activity_message ||
    conversation.value?.messages?.[0] ||
    null
  );
});

const lastMessage = computed(() => {
  if (!lastMessageObj.value) return '';
  return lastMessageObj.value.content || '';
});

const labels = computed(() => {
  const names = conversation.value?.labels || [];
  return names.map(name => {
    const record = allLabels.value.find(l => l.title === name);
    return { name, color: record?.color || '#1f93ff' };
  });
});

const labelNamesTitle = computed(() =>
  labels.value.map(label => label.name).join(', ')
);

const title = computed(
  () =>
    contactName.value ||
    `${t('INTERNAL_CHAT.CONVERSATION_MENTION.PREFIX')}${props.displayId}`
);

const conversationIdLabel = computed(
  () => `${t('INTERNAL_CHAT.CONVERSATION_MENTION.PREFIX')}${props.displayId}`
);

const statusMeta = computed(() => {
  const map = {
    open: {
      icon: 'i-woot-status-open',
      label: t('INTERNAL_CHAT.CONVERSATION_MENTION.STATUS.OPEN'),
      dot: 'bg-n-teal-9',
    },
    resolved: {
      icon: 'i-woot-status-resolved',
      label: t('INTERNAL_CHAT.CONVERSATION_MENTION.STATUS.RESOLVED'),
      dot: 'bg-n-slate-9',
    },
    pending: {
      icon: 'i-woot-status-pending',
      label: t('INTERNAL_CHAT.CONVERSATION_MENTION.STATUS.PENDING'),
      dot: 'bg-n-amber-9',
    },
    snoozed: {
      icon: 'i-woot-status-snoozed',
      label: t('INTERNAL_CHAT.CONVERSATION_MENTION.STATUS.SNOOZED'),
      dot: 'bg-n-blue-9',
    },
  };
  return map[status.value] || map.open;
});

const priority = computed(() => conversation.value?.priority || '');

const priorityMeta = computed(() => {
  const map = {
    urgent: {
      icon: 'i-woot-priority-urgent',
      label: t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
      dot: 'bg-n-ruby-9',
    },
    high: {
      icon: 'i-woot-priority-high',
      label: t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
      dot: 'bg-n-ruby-9',
    },
    medium: {
      icon: 'i-woot-priority-medium',
      label: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
      dot: 'bg-n-amber-9',
    },
    low: {
      icon: 'i-woot-priority-low',
      label: t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
      dot: 'bg-n-blue-9',
    },
  };
  return map[priority.value] || null;
});

const conversationLink = computed(() =>
  frontendURL(
    conversationUrl({ accountId: props.accountId, id: props.displayId })
  )
);

const linkLabel = computed(() =>
  t('INTERNAL_CHAT.CONVERSATION_MENTION.OPEN_CONVERSATION', {
    id: conversationIdLabel.value,
    name: title.value,
  })
);

function navigate() {
  router.push(conversationLink.value);
}

const cardShellClass = computed(() =>
  props.isOwnMessage
    ? 'overflow-hidden rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm border border-dashed border-n-blue-7 bg-transparent'
    : 'overflow-hidden rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm border border-dashed border-n-slate-7 bg-transparent'
);

const cardHeaderClass = computed(() =>
  props.isOwnMessage
    ? 'flex items-center gap-2 border-b border-n-blue-7/40 bg-n-blue-3 px-3 py-2'
    : 'flex items-center gap-2 border-b border-n-slate-7/40 bg-n-slate-3 px-3 py-2'
);

const cardAccentClass = computed(() =>
  props.isOwnMessage ? 'text-n-blue-12' : 'text-n-slate-12'
);

const cardAccentMutedClass = computed(() =>
  props.isOwnMessage ? 'text-n-blue-11' : 'text-n-slate-11'
);

async function fetchConversation() {
  const fromStore = conversationFromStore(
    props.displayId,
    getSelectedChat.value,
    getConversationById.value
  );
  if (fromStore) {
    conversation.value = fromStore;
    conversationCache.set(props.displayId, fromStore);
    fetchFailed.value = false;
    return;
  }

  const cached = conversationCache.get(props.displayId);
  if (cached) {
    conversation.value = cached;
    fetchFailed.value = false;
    return;
  }

  try {
    const data = await loadConversation(props.displayId);
    if (data) {
      conversation.value = data;
      fetchFailed.value = false;
    } else {
      fetchFailed.value = true;
    }
  } catch {
    fetchFailed.value = true;
  }
}

watch(
  () => props.displayId,
  () => {
    conversation.value =
      conversationCache.get(props.displayId) ||
      conversationFromStore(
        props.displayId,
        getSelectedChat.value,
        getConversationById.value
      );
    fetchFailed.value = false;
    fetchConversation();
  }
);

onMounted(fetchConversation);
</script>

<template>
  <div class="mt-2 block w-full min-w-0 first:mt-0">
    <div v-if="fetchFailed" :class="cardShellClass">
      <div :class="cardHeaderClass">
        <Icon
          icon="i-lucide-hash"
          class="size-4 shrink-0"
          :class="cardAccentClass"
        />
        <span class="text-xs font-medium" :class="cardAccentClass">
          {{ t('INTERNAL_CHAT.CONVERSATION_MENTION.LABEL') }}
        </span>
      </div>
      <div class="flex items-center gap-2 px-3 py-2.5 text-sm text-n-slate-10">
        <Icon icon="i-lucide-lock" class="size-4 flex-shrink-0" />
        <span>
          {{ t('INTERNAL_CHAT.CONVERSATION_MENTION.NO_ACCESS') }}
        </span>
      </div>
    </div>
    <div v-else-if="isLoading" :class="cardShellClass">
      <div :class="cardHeaderClass">
        <Icon
          icon="i-lucide-hash"
          class="size-4 shrink-0"
          :class="cardAccentClass"
        />
        <span class="text-xs font-medium" :class="cardAccentClass">
          {{ t('INTERNAL_CHAT.CONVERSATION_MENTION.LABEL') }}
        </span>
        <span
          class="ms-auto shrink-0 tabular-nums text-xs font-medium"
          :class="cardAccentMutedClass"
        >
          {{ conversationIdLabel }}
        </span>
      </div>
      <div class="px-3 py-2.5">
        <div class="h-4 w-2/3 rounded bg-n-alpha-2" />
        <div class="mt-2 h-3 w-full rounded bg-n-alpha-2" />
      </div>
    </div>
    <a
      v-else-if="conversation"
      :href="conversationLink"
      :aria-label="linkLabel"
      class="group block w-full min-w-0 no-underline transition-colors hover:bg-n-alpha-1"
      :class="cardShellClass"
      @click.exact.prevent="navigate"
    >
      <div :class="cardHeaderClass">
        <Icon
          icon="i-lucide-hash"
          class="size-4 shrink-0"
          :class="cardAccentClass"
        />
        <span class="text-xs font-medium" :class="cardAccentClass">
          {{ t('INTERNAL_CHAT.CONVERSATION_MENTION.LABEL') }}
        </span>
        <span
          class="ms-auto shrink-0 tabular-nums text-xs font-medium"
          :class="cardAccentMutedClass"
        >
          {{ conversationIdLabel }}
        </span>
      </div>

      <div class="flex min-w-0 gap-3 px-3 py-2.5">
        <Avatar
          :name="title"
          :src="contactThumbnail"
          :size="28"
          rounded-full
          class="mt-0.5 flex-shrink-0"
        />
        <div class="flex min-w-0 flex-1 flex-col gap-1.5">
          <div class="flex min-w-0 items-baseline gap-2">
            <p
              class="min-w-0 truncate text-sm font-semibold leading-tight text-n-slate-12"
            >
              {{ title }}
            </p>
            <span
              v-if="lastActivityAt"
              class="ms-auto shrink-0 text-xs text-n-slate-10"
            >
              {{ lastActivityAt }}
            </span>
          </div>
          <p
            v-if="lastMessage"
            class="line-clamp-2 text-sm leading-snug text-n-slate-11"
          >
            {{ lastMessage }}
          </p>

          <div class="flex min-w-0 items-center gap-2.5 overflow-hidden">
            <span
              v-tooltip.top="statusMeta.label"
              class="relative inline-flex size-4 flex-shrink-0 items-center justify-center text-n-slate-11"
              :aria-label="statusMeta.label"
            >
              <Icon :icon="statusMeta.icon" class="size-3.5" />
              <span
                class="absolute -end-px -top-px size-1.5 rounded-full"
                :class="statusMeta.dot"
              />
            </span>

            <span
              v-if="priorityMeta"
              v-tooltip.top="priorityMeta.label"
              class="relative inline-flex size-4 flex-shrink-0 items-center justify-center text-n-slate-11"
              :aria-label="priorityMeta.label"
            >
              <Icon :icon="priorityMeta.icon" class="size-3.5" />
              <span
                class="absolute -end-px -top-px size-1.5 rounded-full"
                :class="priorityMeta.dot"
              />
            </span>

            <span
              v-if="labels.length"
              v-tooltip.top="labelNamesTitle"
              class="flex min-w-0 flex-shrink items-center gap-1 overflow-hidden text-n-slate-11"
              :aria-label="labelNamesTitle"
            >
              <span
                class="relative inline-flex size-4 flex-shrink-0 items-center justify-center"
              >
                <Icon icon="i-lucide-tags" class="size-3.5" />
                <span
                  class="absolute -end-0.5 -top-0.5 min-w-1.5 rounded-full bg-n-slate-12 px-0.5 text-center text-[0.5625rem] leading-3 text-n-solid-1"
                >
                  {{ labels.length }}
                </span>
              </span>
              <span
                v-for="label in labels"
                :key="label.name"
                class="size-1.5 flex-shrink-0 rounded-full"
                :style="{ backgroundColor: label.color }"
              />
            </span>

            <span
              v-if="assigneeName"
              v-tooltip.top="assigneeName"
              class="relative inline-flex size-4 flex-shrink-0 items-center justify-center text-n-slate-11"
              :aria-label="assigneeName"
            >
              <Icon icon="i-lucide-user" class="size-3.5" />
              <span
                class="absolute -end-px -top-px size-1.5 rounded-full bg-n-brand"
              />
            </span>

            <span
              v-if="inboxName"
              v-tooltip.top="inboxName"
              class="relative inline-flex size-4 flex-shrink-0 items-center justify-center text-n-slate-11"
              :aria-label="inboxName"
            >
              <Icon icon="i-lucide-inbox" class="size-3.5" />
              <span
                class="absolute -end-px -top-px size-1.5 rounded-full bg-n-slate-9"
              />
            </span>
          </div>
        </div>
      </div>

      <div
        class="flex items-center gap-1 border-t border-n-slate-6 px-3 py-2 text-xs font-medium text-n-slate-11 group-hover:text-n-slate-12 group-hover:underline"
      >
        <Icon icon="i-lucide-external-link" class="size-3" />
        {{ t('INTERNAL_CHAT.CONVERSATION_MENTION.VIEW') }}
      </div>
    </a>
  </div>
</template>
